<template>
  <b-card>
    <b-card-header :class="{ 'critical-header': dangerOnPage }">
      <h4 class="mb-0">
        <feather-icon
          icon="ArchiveIcon"
          size="20"
          class="mr-50"
        />
        {{ $t('delivery.backorder.title', { lb: lbNo }) }}
        <b-badge
          v-if="totalRows > 0"
          variant="secondary"
          class="ml-50"
        >
          {{ totalRows }}
        </b-badge>
      </h4>
      <p class="mb-0 text-muted">
        {{ $t('delivery.backorder.desc') }}
      </p>
      <div style="margin-left: auto;">
        <feather-icon
          icon="DownloadIcon"
          size="21"
          class="text-muted cursor-pointer"
          @click="exportXlsx"
        />
      </div>
    </b-card-header>
    <b-card-body class="card-body-table">
      <b-row class="mb-1 px-1 align-items-center">
        <b-col md="5">
          <b-form-input
            v-model="search"
            type="search"
            size="sm"
            :placeholder="$t('delivery.backorder.searchPlaceholder')"
            @change="applySearch"
          />
        </b-col>
        <b-col
          md="7"
          class="d-flex align-items-center"
        >
          <b-form-checkbox
            v-model="vorStockOnly"
            switch
            @change="applySearch"
          >
            {{ $t('delivery.backorder.vorStockOnly') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-overlay
        :show="loading"
        spinner-variant="primary"
        variant="transparent"
        blur="3px"
        rounded="sm"
      >
        <b-table
          striped
          hover
          responsive
          small
          class="position-relative"
          :items="items"
          :fields="fields"
          :tbody-tr-class="rowClass"
          show-empty
          :empty-text="$t('delivery.backorder.empty')"
        >
          <template #cell(item_sku)="row">
            <div class="font-weight-bold">
              {{ row.item.item_sku }}
            </div>
            <small class="text-muted">{{ row.item.name }}</small>
          </template>
          <template #head(added_date)="head">
            <span
              v-b-tooltip.hover
              :title="$t('delivery.backorder.addedTip')"
            >{{ head.label }}</span>
          </template>
          <template #cell(added_date)="row">
            <span
              v-b-tooltip.hover
              :title="$t('delivery.backorder.addedTip')"
            >{{ fmtDate(row.value) }}</span>
          </template>
          <template #cell(age_days)="row">
            <b-badge :variant="ageVariant(row.item.age_level)">
              {{ row.value }} {{ $t('delivery.common.daysShort') }}
            </b-badge>
          </template>
          <template #cell(supplier_stock)="row">
            <div>{{ fmtQty(row.value) }}</div>
            <small
              v-if="row.item.supplier_stock_updated"
              class="text-muted"
            >
              {{ $t('delivery.backorder.updated', { date: fmtDate(row.item.supplier_stock_updated) }) }}
            </small>
            <div v-if="row.item.supplier_has_stock">
              <b-badge variant="success">
                {{ $t('delivery.backorder.vorHasStock') }}
              </b-badge>
            </div>
            <div v-else-if="partialStock(row.item)">
              <b-badge variant="warning">
                {{ $t('delivery.backorder.vorPartial', { stock: fmtQty(row.value), qty: fmtQty(row.item.open_qty) }) }}
              </b-badge>
            </div>
          </template>
          <template #cell(supplier_lead_days)="row">
            {{ row.value ? `${row.value} ${$t('delivery.common.daysShort')}` : '—' }}
          </template>
          <template #cell(other_suppliers)="row">
            <small>{{ row.value || '—' }}</small>
          </template>
          <template #cell(waiting_orders)="row">
            <span v-if="row.value">
              {{ row.value }}
              <small class="text-muted d-block">
                {{ $t('delivery.backorder.oldest', { order: row.item.oldest_waiting_order_no }) }}
              </small>
            </span>
            <span v-else>—</span>
          </template>
          <template #cell(delivery_date)="row">
            {{ fmtDate(row.value) || '—' }}
          </template>
          <template #cell(note)="row">
            <small
              style="display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; min-width: 240px; max-width: 320px;"
              :title="row.value || ''"
            >{{ row.value || '' }}</small>
          </template>
        </b-table>
      </b-overlay>
      <div class="d-flex justify-content-between flex-wrap pt-0 pagination-wrapper">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="10"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          class="mb-0"
          @change="pageChanged"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BFormInput, BFormCheckbox, BOverlay, BTable, BBadge, BPagination, VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { fmtDate, fmtQty, exportBackorderXlsx } from './backorderExport'

const API = process.env.VUE_APP_ROOT_API

// age_level comes from the API (thresholds live in the backend only).
const AGE_VARIANTS = { 2: 'danger', 1: 'warning' }

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BFormCheckbox,
    BOverlay,
    BTable,
    BBadge,
    BPagination,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  // From the purchasing summary, so the title keeps it when the list is empty.
  props: {
    lbNo: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      items: [],
      totalRows: 0,
      currentPage: 1,
      search: '',
      vorStockOnly: false,
    }
  },
  computed: {
    fields() {
      const t = key => this.$t(`delivery.backorder.cols.${key}`)
      return [
        { key: 'item_sku', label: t('article') },
        { key: 'open_qty', label: t('openQty'), formatter: v => this.fmtQty(v) },
        { key: 'added_date', label: t('added') },
        { key: 'age_days', label: t('age') },
        { key: 'supplier_stock', label: t('vorStock') },
        { key: 'supplier_lead_days', label: t('vorLeadTime') },
        { key: 'own_stock', label: t('ownStock'), formatter: v => this.fmtQty(v) },
        { key: 'other_suppliers', label: t('otherSuppliers') },
        { key: 'waiting_orders', label: t('waitingOrders') },
        { key: 'delivery_date', label: t('lbDate') },
        { key: 'note', label: t('note') },
      ]
    },
    dangerOnPage() {
      return this.items.some(i => i.age_level === 2)
    },
  },
  methods: {
    // Called by the dashboard's refreshAll so page load / Refresh fetch it once.
    async refresh() {
      this.loading = true
      try {
        const results = await this.fetchRows({ page: this.currentPage })
        this.items = results
        this.totalRows = results.length ? results[0].count : 0
      } catch (e) {
        this.notifyError()
      } finally {
        this.loading = false
      }
    },
    async fetchRows(extra) {
      const params = { ...extra }
      if (this.search) params.search = `%${this.search.toLowerCase()}%`
      if (this.vorStockOnly) params.vor_stock = '1'
      const res = await axios.get(`${API}/delivery-dashboard/lb-backorder/`, {
        headers: { Authorization: `JWT ${this.$store.state.jwt}`, 'Content-Type': 'application/json' },
        params,
      })
      return res.data.results || []
    },
    // Same export=true + XLSX pattern as the dashboard's other lists (search and VOR-stock filter apply).
    async exportXlsx() {
      try {
        exportBackorderXlsx(await this.fetchRows({ export: 'true' }), (...a) => this.$t(...a))
      } catch (e) {
        this.notifyError()
      }
    },
    notifyError() {
      this.$toast({
        component: ToastificationContent,
        props: { title: this.$t('delivery.backorder.loadError'), icon: 'AlertTriangleIcon', variant: 'danger' },
      })
    },
    applySearch() {
      this.currentPage = 1
      this.refresh()
    },
    pageChanged(page) {
      this.currentPage = page
      this.refresh()
    },
    rowClass(item) {
      return item && item.supplier_has_stock ? 'table-success' : ''
    },
    // Not a green row: VOR has some, but less than the open quantity.
    partialStock(item) {
      const stock = Number(item.supplier_stock)
      return stock > 0 && stock < Number(item.open_qty)
    },
    ageVariant(level) {
      return AGE_VARIANTS[level] || 'light-secondary'
    },
    fmtDate,
    fmtQty,
  },
}
</script>
